import { t } from "@lingui/core/macro";
import {
  AllAllowedFileTypes,
  EMPTY_STRING,
  endOfDay,
  type FrontendAllowedFileTypes,
  getAllowedMapForExtensions,
} from "@regrello/core-utils";
import type {
  PropertyTypeFields,
  SpectrumFieldConstraintFields,
  SpectrumFieldVersionFields,
  SpectrumValueConstraintFields,
} from "@regrello/graphql-api";
import { startOfDay } from "date-fns";
import { ValidationRules } from "../../../../../constants/globalConstants";

export enum FrontendValueConstraintRuleName {
  DECIMAL_PLACES = "decimal_places",
  DISALLOW_GROUPINGS_FLOAT = "disallow_groupings_float",

  MAX_CHARACTER = "max_characters",
  MIN_CHARACTER = "min_characters",
  MARKDOWN_FORMATTED = "markdown_formatted",
  REGEX_VALIDATED = "regex_validated",

  MAX_INT = "max_int",
  MIN_INT = "min_int",

  MAX_FLOAT = "max_float",
  MIN_FLOAT = "min_float",

  MAX_PARTY = "max_parties",

  BEFORE_OR_AFTER_TODAY_INCLUSIVE = "before_or_after_today_inclusive",

  EMAIL = "email",

  DOCUMENT_TYPE = "document_type",
  DOCUMENT_DISALLOW_LINK = "document_disallow_link",
}

export const ON_OR_AFTER_TODAY_ARG = "1";
export const ON_OR_BEFORE_TODAY_ARG = "-1";

/** Max party is -1, BE will ignore this constraint. */
export const ALLOW_MULTIPLE_PARTY_ARG = "-1";

/** Max party is 1. */
export const DISALLOW_MULTIPLE_PARTY_ARG = "1";

/**
 * Returns the number of decimal places configured in the provided field version (assuming it's
 * stored as the field constraint).
 *
 * @param fieldVersion - The field version to get the decimal places from.
 * @returns The number of decimal places, or undefined if the decimal places constraint is not found
 */
export function getDecimalPlacesFromFieldVersion(
  fieldVersion: Pick<SpectrumFieldVersionFields, "fieldConstraints">,
): number | undefined {
  const constraints = fieldVersion.fieldConstraints;

  if (constraints == null || constraints.length === 0) {
    return undefined;
  }

  const decimalPlacesConstraint = constraints.find(
    (fieldConstraint) =>
      fieldConstraint.spectrumValueConstraint.valueConstraintRule === FrontendValueConstraintRuleName.DECIMAL_PLACES,
  );

  if (decimalPlacesConstraint == null) {
    return undefined;
  }

  // Extract the decimal places value from the constraint.
  const decimalPlaces = Number.parseInt(decimalPlacesConstraint.constraintArgs[0], 10);
  if (Number.isNaN(decimalPlaces) || decimalPlaces < 0) {
    return undefined;
  }

  return decimalPlaces;
}

/**
 * Returns whether the "disallow_groupings_float" flag is true or false in the provided field
 * version. If true, the value should appear with thousands separators. If false, thousands
 * separators should not be shown.
 *
 * @param fieldVersion - The field version to get the disallow_groupings_float from.
 * @returns The disallow_groupings_float value, or undefined if it is not configured.
 */
export function getDisallowGroupingsFloatFromFieldVersion(
  fieldVersion: Pick<SpectrumFieldVersionFields, "fieldConstraints"> & {
    propertyType: Pick<PropertyTypeFields, "dataType">;
  },
): boolean | undefined {
  if (fieldVersion.propertyType?.dataType === "INT") {
    // (clewis): Always disable separators for legacy INT-valued number fields. We do this because
    // there are already untold numbers of INT-valued number field versions out in the wild on prod,
    // and customers have expressed they don't want us to introduce separators for many of those (PO
    // numbers, zip codes, etc.). Rather than generate a separate disallow_groupings_int constraint
    // for every INT-valued Number Field version, just prevent separators for those fields.
    return true;
  }

  const constraints = fieldVersion.fieldConstraints;

  if (constraints == null || constraints.length === 0) {
    return undefined;
  }

  const targetConstraint = constraints.find(
    (fieldConstraint) =>
      fieldConstraint.spectrumValueConstraint.valueConstraintRule ===
      FrontendValueConstraintRuleName.DISALLOW_GROUPINGS_FLOAT,
  );

  if (targetConstraint == null) {
    return undefined;
  }

  const argValue = targetConstraint.constraintArgs[0];
  if (argValue === "true") {
    return true;
  }
  if (argValue === "false") {
    return false;
  }
  return undefined;
}

/**
 * Returns a validation function for the given field constraint.
 *
 * @param fieldConstraint - The field constraint to get the validation function from.
 * @returns The validation function, or undefined if the field constraint is not a validation rule.
 */
export function getValidationFunctionFromFieldConstraint(
  fieldConstraint: SpectrumFieldConstraintFields,
): ((value: unknown) => true | string) | undefined {
  const constraintArgs = fieldConstraint.constraintArgs;
  const valueConstraintRule = fieldConstraint.spectrumValueConstraint.valueConstraintRule;
  switch (valueConstraintRule) {
    case FrontendValueConstraintRuleName.DECIMAL_PLACES:
      // The number of decimal places will be enforced on the frontend by the RegrelloNumericInput
      // component (or a wrapper of it), not by a form-level validation rule.
      return undefined;

    case FrontendValueConstraintRuleName.DISALLOW_GROUPINGS_FLOAT:
      // This is strictly a presentational constraint whose format will be automatically applied by
      // the RegrelloNumericInput component, not by a form-level validation rule. It is only
      // relevant in Works Thru Email, Bulk Start Workflows, and similar use cases.
      return undefined;

    case FrontendValueConstraintRuleName.MARKDOWN_FORMATTED:
      // This is strictly a presentational constraint, when set to true we will assume the content
      // contains markdown formatted via react-markdown.
      return undefined;

    case FrontendValueConstraintRuleName.MIN_CHARACTER:
      return (value: unknown) => {
        if (value === null || typeof value !== "string") {
          return t`Invalid value type`;
        }
        const minLength = Number.parseInt(constraintArgs[0], 10);
        return ValidationRules.MIN_LENGTH_FN(minLength).validate(value);
      };

    case FrontendValueConstraintRuleName.MAX_CHARACTER:
      return (value: unknown) => {
        if (value === null || typeof value !== "string") {
          return t`Invalid value type`;
        }
        const maxLength = Number.parseInt(constraintArgs[0], 10);
        return ValidationRules.MAX_LENGTH_FN(maxLength).validate(value);
      };

    case FrontendValueConstraintRuleName.REGEX_VALIDATED:
      return (value: unknown) => {
        if (value === null || typeof value !== "string") {
          return t`Invalid value type`;
        }

        const pattern = constraintArgs[1];
        const valid = ValidationRules.REGEX_FN(pattern).validate(value);
        if (!valid) {
          return constraintArgs[0];
        }
        return valid;
      };

    case FrontendValueConstraintRuleName.MIN_FLOAT:
      return (value: unknown) => {
        if (value === null || typeof value !== "number") {
          return t`Invalid value type`;
        }
        const minValue = Number.parseFloat(constraintArgs[0]);
        return ValidationRules.GREATER_THAN_OR_EQUAL_TO_LOWER_BOUND_FN(minValue).validate(value);
      };

    case FrontendValueConstraintRuleName.MAX_FLOAT:
      return (value: unknown) => {
        if (value === null || typeof value !== "number") {
          return t`Invalid value type`;
        }
        const maxValue = Number.parseFloat(constraintArgs[0]);
        return ValidationRules.LESS_THAN_OR_EQUAL_TO_UPPER_BOUND_FN(maxValue).validate(value);
      };

    case FrontendValueConstraintRuleName.MIN_INT:
      return (value: unknown) => {
        if (value === null || typeof value !== "number") {
          return t`Invalid value type`;
        }
        const minValue = Number.parseInt(constraintArgs[0], 10);
        return ValidationRules.GREATER_THAN_OR_EQUAL_TO_LOWER_BOUND_FN(minValue).validate(value);
      };

    case FrontendValueConstraintRuleName.MAX_INT:
      return (value: unknown) => {
        if (value === null || typeof value !== "number") {
          return t`Invalid value type`;
        }
        const maxValue = Number.parseInt(constraintArgs[0], 10);
        return ValidationRules.LESS_THAN_OR_EQUAL_TO_UPPER_BOUND_FN(maxValue).validate(value);
      };

    case FrontendValueConstraintRuleName.EMAIL:
      return (value: unknown) => {
        if (value === null || typeof value !== "string") {
          return t`Invalid value type`;
        }
        return ValidationRules.VALID_EMAIL.validate(value);
      };

    case FrontendValueConstraintRuleName.BEFORE_OR_AFTER_TODAY_INCLUSIVE:
      return (value: unknown) => {
        if (constraintArgs.length === 0) {
          return true;
        }
        if (value !== null && !(value instanceof Date)) {
          return t`Invalid value type`;
        }
        if (Number.parseInt(constraintArgs[0], 10) > 0) {
          return ValidationRules.AFTER_DATE(startOfDay(new Date())).validate(value);
        }
        return ValidationRules.BEFORE_DATE(endOfDay(new Date())).validate(value);
      };

    case FrontendValueConstraintRuleName.DOCUMENT_TYPE:
      return (value: unknown) => {
        if (!Array.isArray(value)) {
          return t`Invalid value type`;
        }

        const allowedFormats = constraintArgs.filter((arg) =>
          AllAllowedFileTypes.includes(arg as FrontendAllowedFileTypes),
        ) as FrontendAllowedFileTypes[];

        const castedValue = value as Array<{ name: string; currentVersion: { externalLink?: string } }>;

        return ValidationRules.VALID_DOCUMENT_FORMAT(allowedFormats).validate(castedValue);
      };

    default:
      return () => true;
  }
}

export function getValueConstraintDisplayName(valueConstraint: SpectrumValueConstraintFields) {
  switch (valueConstraint.valueConstraintRule) {
    // (clewis): Keep cases alphabetized.
    case FrontendValueConstraintRuleName.BEFORE_OR_AFTER_TODAY_INCLUSIVE:
      return t`Accepted dates`;
    case FrontendValueConstraintRuleName.DECIMAL_PLACES:
      return t`Decimal places`;
    case FrontendValueConstraintRuleName.DISALLOW_GROUPINGS_FLOAT:
      return t`Separators`;
    case FrontendValueConstraintRuleName.DOCUMENT_DISALLOW_LINK:
      return t`Disallow external link`;
    case FrontendValueConstraintRuleName.DOCUMENT_TYPE:
      return t`File extensions`;
    case FrontendValueConstraintRuleName.MARKDOWN_FORMATTED:
      return t`Enable formatting`;
    case FrontendValueConstraintRuleName.MAX_CHARACTER:
      return t`Maximum length`;
    case FrontendValueConstraintRuleName.MAX_FLOAT:
      return t`Maximum value`;
    case FrontendValueConstraintRuleName.MAX_INT:
      return t`Maximum value`;
    case FrontendValueConstraintRuleName.MAX_PARTY:
      return t`Allow adding multiple people or teams`;
    case FrontendValueConstraintRuleName.MIN_CHARACTER:
      return t`Minimum length`;
    case FrontendValueConstraintRuleName.MIN_FLOAT:
      return t`Minimum value`;
    case FrontendValueConstraintRuleName.MIN_INT:
      return t`Minimum value`;
    case FrontendValueConstraintRuleName.REGEX_VALIDATED:
      return t`Prompt`;
    default:
      return EMPTY_STRING;
  }
}

export function getValueConstraintDisplayValue(
  fieldConstraint: Pick<SpectrumFieldConstraintFields, "spectrumValueConstraint" | "constraintArgs">,
) {
  const joinWithCommas = (args: string[]) => args.join(", ");

  switch (fieldConstraint.spectrumValueConstraint.valueConstraintRule) {
    // (clewis): Keep cases alphabetized.
    case FrontendValueConstraintRuleName.BEFORE_OR_AFTER_TODAY_INCLUSIVE:
      return joinWithCommas(fieldConstraint.constraintArgs);
    case FrontendValueConstraintRuleName.DECIMAL_PLACES:
      return joinWithCommas(fieldConstraint.constraintArgs);
    case FrontendValueConstraintRuleName.DISALLOW_GROUPINGS_FLOAT:
      return fieldConstraint.constraintArgs[0] === "true" ? t`Disabled` : t`Enabled`;
    case FrontendValueConstraintRuleName.DOCUMENT_DISALLOW_LINK:
      return joinWithCommas(fieldConstraint.constraintArgs);
    case FrontendValueConstraintRuleName.DOCUMENT_TYPE:
      return joinWithCommas(fieldConstraint.constraintArgs);
    case FrontendValueConstraintRuleName.MARKDOWN_FORMATTED:
      return joinWithCommas(fieldConstraint.constraintArgs);
    case FrontendValueConstraintRuleName.MAX_CHARACTER:
      return joinWithCommas(fieldConstraint.constraintArgs);
    case FrontendValueConstraintRuleName.MAX_FLOAT:
      return joinWithCommas(fieldConstraint.constraintArgs);
    case FrontendValueConstraintRuleName.MAX_INT:
      return joinWithCommas(fieldConstraint.constraintArgs);
    case FrontendValueConstraintRuleName.MAX_PARTY:
      return joinWithCommas(fieldConstraint.constraintArgs);
    case FrontendValueConstraintRuleName.MIN_CHARACTER:
      return joinWithCommas(fieldConstraint.constraintArgs);
    case FrontendValueConstraintRuleName.MIN_FLOAT:
      return joinWithCommas(fieldConstraint.constraintArgs);
    case FrontendValueConstraintRuleName.MIN_INT:
      return joinWithCommas(fieldConstraint.constraintArgs);
    case FrontendValueConstraintRuleName.REGEX_VALIDATED:
      return joinWithCommas(fieldConstraint.constraintArgs);
    default:
      return EMPTY_STRING;
  }
}

export function getIsValueConstraintEmpty(valueConstraint: SpectrumValueConstraintFields, args: string[]) {
  if (args.length === 0) {
    return true;
  }

  switch (valueConstraint.valueConstraintRule) {
    case FrontendValueConstraintRuleName.DOCUMENT_DISALLOW_LINK:
      return args[0] === "false";
    case FrontendValueConstraintRuleName.BEFORE_OR_AFTER_TODAY_INCLUSIVE:
      return args[0] === "0";
    case FrontendValueConstraintRuleName.MAX_PARTY:
      return args[0] === "1";
    default:
      return args.every((arg) => arg.length === 0);
  }
}

export function getNumberOfArgsByConstraint(valueConstraint: SpectrumValueConstraintFields) {
  switch (valueConstraint.valueConstraintRule) {
    case FrontendValueConstraintRuleName.DECIMAL_PLACES:
    case FrontendValueConstraintRuleName.DISALLOW_GROUPINGS_FLOAT:
    case FrontendValueConstraintRuleName.MIN_CHARACTER:
    case FrontendValueConstraintRuleName.MAX_CHARACTER:
    case FrontendValueConstraintRuleName.MARKDOWN_FORMATTED:
    case FrontendValueConstraintRuleName.MIN_INT:
    case FrontendValueConstraintRuleName.MAX_INT:
    case FrontendValueConstraintRuleName.MIN_FLOAT:
    case FrontendValueConstraintRuleName.MAX_FLOAT:
    case FrontendValueConstraintRuleName.MAX_PARTY:
    case FrontendValueConstraintRuleName.BEFORE_OR_AFTER_TODAY_INCLUSIVE:
    case FrontendValueConstraintRuleName.DOCUMENT_DISALLOW_LINK:
      return 1;
    case FrontendValueConstraintRuleName.REGEX_VALIDATED:
      return 2;
    case FrontendValueConstraintRuleName.EMAIL:
      return 0;
    default:
      return 0;
  }
}

export interface FrontendActiveRules {
  hide: boolean;
  required: boolean;
  show: boolean;
}

export function getActiveRuleAsBoolean(
  spectrumRuleMetadata: Array<{ type: string; value: string }> | undefined,
): FrontendActiveRules {
  const rules: FrontendActiveRules = {
    show: false,
    hide: false,
    required: false,
  };
  if (spectrumRuleMetadata == null) {
    return rules;
  }
  for (const { value } of spectrumRuleMetadata) {
    switch (value) {
      case "hide":
        rules.hide = true;
        break;
      case "show":
        rules.show = true;
        break;
      case "mandatory":
        rules.required = true;
        rules.show = true;
        break;
      default:
        break;
    }
  }
  return rules;
}

export function getAllowedFileTypes(spectrumFieldVersion: SpectrumFieldVersionFields) {
  for (const constraint of spectrumFieldVersion.fieldConstraints) {
    if (constraint.spectrumValueConstraint.valueConstraintRule === FrontendValueConstraintRuleName.DOCUMENT_TYPE) {
      return getAllowedMapForExtensions(constraint.constraintArgs as FrontendAllowedFileTypes[]);
    }
  }
  return {};
}

export function getIsLinkDisallowed(spectrumFieldVersion: SpectrumFieldVersionFields) {
  for (const constraint of spectrumFieldVersion.fieldConstraints) {
    if (
      constraint.spectrumValueConstraint.valueConstraintRule === FrontendValueConstraintRuleName.DOCUMENT_DISALLOW_LINK
    ) {
      return constraint.constraintArgs.length > 0 && constraint.constraintArgs[0] === "true";
    }
  }
  return false;
}

export function getIsMultiPartyAllowed(spectrumFieldVersion: SpectrumFieldVersionFields) {
  for (const constraint of spectrumFieldVersion.fieldConstraints) {
    if (constraint.spectrumValueConstraint.valueConstraintRule === FrontendValueConstraintRuleName.MAX_PARTY) {
      return constraint.constraintArgs.length > 0 && constraint.constraintArgs[0] === ALLOW_MULTIPLE_PARTY_ARG;
    }
  }
  return false;
}

/**
 * Validates the given value against the given Spectrum field constraints.
 *
 * @param constraints - The constraints to validate the value against.
 * @param value - The value to validate.
 * @returns The validation result.
 */
export function validateByConstraints(
  constraints: SpectrumFieldConstraintFields[],
  value: unknown,
): { isValid: boolean; errors: string[] } {
  const validationFns = constraints.map(getValidationFunctionFromFieldConstraint);

  const errors: string[] = [];
  for (const validationFn of validationFns) {
    if (validationFn != null) {
      const result = validationFn(value);
      if (result !== true) {
        errors.push(result);
      }
    }
  }

  return { isValid: errors.length === 0, errors };
}
